import { useCallback, useRef } from 'react';
import { Auth } from 'aws-amplify';
import { shutdown } from 'Services/intercom';
import { useSelector } from 'react-redux';
import { selectLogoutRedirectUrl } from 'state/Organization/organizationSlice';
import { setPendingLogoutRedirect } from 'utils/logoutRedirectStorage';
import { PATHS } from 'utils/constants';
import config from 'config/Config';

/**
 * Calls DELETE /v2/auth/session-cache to remove the user's Redis cache entries
 * (`auth:user:{sub}` and `auth:user-id-to-sub:{id}`) on the backend so the
 * session is invalidated immediately rather than waiting for the 8-hour TTL.
 * Best-effort — a network failure must never block the logout flow.
 */
const clearBackendSession = async (jwtToken: string): Promise<void> => {
  try {
    const response = await fetch(
      `${config.get('backendURL') as string}v2/auth/session-cache`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${jwtToken}` },
      }
    );
    if (!response.ok) {
      // eslint-disable-next-line no-console -- surface non-2xx for debugging; logout still proceeds
      console.warn('clearBackendSession: unexpected status', response.status);
    }
  } catch {
    // Network failure — logout proceeds regardless.
  }
};

const clearAmplifyAuthStorage = () => {
  const keysToRemove = Object.keys(localStorage).filter(
    (key) =>
      key.startsWith('CognitoIdentityServiceProvider') ||
      key.startsWith('amplify-')
  );
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

export const useLogout = () => {
  const redirectUrl = useSelector(selectLogoutRedirectUrl);
  const isLoggingOut = useRef(false);

  const handleLogout = useCallback(async () => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    const customRedirect =
      typeof redirectUrl === 'string' && redirectUrl.trim().length > 0
        ? redirectUrl.trim()
        : null;

    try {
      const user = await Auth.currentAuthenticatedUser();
      const identities = user?.signInUserSession?.idToken?.payload?.identities;
      const isFederated = Array.isArray(identities) && identities.length > 0;
      const jwtToken: string | undefined =
        user?.signInUserSession?.idToken?.jwtToken;

      shutdown();

      if (isFederated) {
        // For ALL federated (SSO/SAML) users, hit Cognito's /logout endpoint
        // directly. Auth.signOut() does not reliably clear the IdP session for
        // SAML/OIDC federated logins — the Cognito hosted-UI logout endpoint is
        // the correct mechanism for both US and CA prod regions.
        // Only stash the org redirect when one is configured; Cognito will land
        // back on redirectSignOut (/login) either way.
        if (customRedirect) setPendingLogoutRedirect(customRedirect);
        // Clear the Redis session cache before wiping local storage so the
        // JWT is still in memory when the backend call is made.
        if (jwtToken) await clearBackendSession(jwtToken);
        clearAmplifyAuthStorage();

        const cognitoDomain = config.get(
          'authentication.oauthDomain'
        ) as string;
        const clientId = (config.get('authentication.userPoolWebClientId') ||
          process.env.REACT_APP_COGNITO_POOL_CLIENT) as string;
        // Use redirectSignOut (/login) — not redirectSignIn (/callback) — so
        // Cognito lands back on the Login page where takePendingLogoutRedirect()
        // reads sessionStorage and forwards the user to the org's custom URL.
        const landingUrl = encodeURIComponent(
          config.get('authentication.redirectSignOut') as string
        );
        const logoutUrl = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${landingUrl}`;

        globalThis.location.replace(logoutUrl);
        return;
      }

      // For non-federated users, clear the Redis cache while the JWT is still
      // valid, then revoke the server-side Cognito session and clear local state.
      // Stash the org redirect (if any) so the Login page can forward the user
      // after they sign back in — same pattern as the federated path.
      if (customRedirect) setPendingLogoutRedirect(customRedirect);
      if (jwtToken) await clearBackendSession(jwtToken);
      await Auth.signOut({ global: true });
      clearAmplifyAuthStorage();
      globalThis.location.replace(PATHS.LOGIN);
    } catch (err) {
      // eslint-disable-next-line no-console -- log logout failure before navigating away
      console.error('Logout failed', err);
      clearAmplifyAuthStorage();
      globalThis.location.replace(PATHS.LOGIN);
    }
  }, [redirectUrl, isLoggingOut]);

  return handleLogout;
};

export default useLogout;
